import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import profile from "../images/profile.jpg";
import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { permissionsOf, roleLabel } from "../auth/permissions";
import { clearActiveBusinessId } from "../auth/tokenStore";
import UsersDirectory from "../components/UsersDirectory";
import "../overview.css";

/* ------------------------------------------------------------------ *
 * Group users — /overview/users
 *
 * The users page for the whole group: everyone across every business the
 * signed-in person belongs to, reached from the group overview. Sits
 * outside any business (same standalone chrome as /overview, no
 * `business-id` header by default).
 *
 * DATA — GET /group/users
 *   { success, task: 'group', data: { businesses: [{ business_id, name,
 *     code, can_manage }], users: [{ _id, full_name, userName, thumb,
 *     active, date, memberships: [{ business_id, role, permissions,
 *     custom_permissions, created_at }] }] } }
 *
 * WRITES go to the ordinary per-business endpoints (/users/addNew,
 * /users/editUser, /users/removeUser/:id) with an explicit `business-id`
 * header naming the membership being changed, so the server applies the
 * caller's role in THAT business. `can_manage` mirrors that check for the
 * UI; the server re-checks on every write.
 * ------------------------------------------------------------------ */

const inBusiness = (bizId) => ({ headers: { "business-id": String(bizId) } });

export function GroupUsersPage() {
  const auth = useAuth() || {};
  const user = auth.user || {};
  const navigate = useNavigate();

  // Outside any business: drop the active id so nothing in flight carries a
  // stale header (mirrors OverviewPage).
  useEffect(() => {
    clearActiveBusinessId();
  }, []);

  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${BASE_SERVER_URL}/group/users`)
      .then((res) => {
        const data = res && res.data && res.data.data;
        if (!data) throw new Error("Malformed response");
        setBusinesses(
          (data.businesses || []).map((b) => ({
            id: String(b.business_id),
            name: b.name,
            code: b.code,
            can_manage: Boolean(b.can_manage),
          }))
        );
        setUsers(
          (data.users || []).map((u) => ({
            id: String(u._id),
            full_name: u.full_name,
            email: u.email,
            userName: u.userName,
            thumb: u.thumb,
            active: u.active,
            date: u.date,
            memberships: (u.memberships || []).map((m) => ({
              ...m,
              business_id: String(m.business_id),
            })),
          }))
        );
      })
      .catch((err) => {
        setUsers([]);
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "The server didn't answer."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* What the caller may grant in a business = their own effective set
     there, from the session's memberships (the server refuses anything
     beyond it). */
  const grantableIn = (bizId) => {
    const m = (auth.memberships || []).find((x) => String(x.business_id) === String(bizId));
    return m ? permissionsOf({ role: m.role, permissions: m.permissions }) : [];
  };

  const api = {
    add: (biz, detail) =>
      axios.post(`${BASE_SERVER_URL}/users/addNew`, { userDetail: detail }, inBusiness(biz)),
    edit: (biz, id, updateData) =>
      axios.post(`${BASE_SERVER_URL}/users/editUser`, { id, updateData }, inBusiness(biz)),
    remove: (biz, id) =>
      axios.get(`${BASE_SERVER_URL}/users/removeUser/${id}`, inBusiness(biz)),
  };

  async function handleLogout() {
    if (auth.logout) await auth.logout();
  }

  const displayName = user.full_name || user.userName || "—";
  const managed = businesses.filter((b) => b.can_manage).length;
  const lede =
    businesses.length === 0
      ? "Everyone across the group."
      : managed === businesses.length
      ? `Everyone across ${businesses.length === 1 ? "your business" : `all ${businesses.length} businesses`}. Pick a person to change their role per business.`
      : `Everyone across ${businesses.length} businesses. You can manage people in ${managed} of them; the rest are view only.`;

  return (
    <div className="overview-page">
      {/* Toasts come from the AppShell's container, which stays mounted
          even in bare chrome mode. */}
      <div className="ov-topbar">
        <div className="ov-topbar-brand">
          <button
            type="button"
            className="ov-btn ov-btn-ghost us-topbar-back"
            onClick={() => navigate("/overview")}
          >
            ‹ Overview
          </button>
          <span className="ov-rule-v" />
          <span className="ov-serif ov-topbar-title">Group staff</span>
        </div>

        <span className="ov-spacer" />

        <div className="ov-user-wrap">
          <div className="ov-user" style={{ cursor: "default" }}>
            <img src={profile} alt="" />
            <div>
              <div className="ov-user-name">{displayName}</div>
              <div className="ov-user-role">{roleLabel(user)}</div>
            </div>
          </div>
        </div>
        <button type="button" className="ov-btn ov-btn-ghost" onClick={handleLogout}>
          Log out
        </button>
      </div>

      <UsersDirectory
        mode="group"
        standalone
        title="People across the group"
        lede={lede}
        businesses={businesses}
        users={users}
        loading={loading}
        error={error}
        reload={load}
        currentUser={user}
        grantableIn={grantableIn}
        api={api}
      />
    </div>
  );
}

export default GroupUsersPage;
